import { Link as RouterLink } from 'react-router-dom';
import { useDisplayPreferences } from '@/shared/hooks/useDisplayPreferences';
import { fontFamilies, r, s } from '@/theme/tokens';
import { Chip, Inline, Text } from '@/ui';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, Link as MuiLink, Tooltip } from '@/ui/primitives';
import { linkTruncateSx } from '../cellContents/shared';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '../tableTooltip';
import { OverflowTooltip } from './OverflowTooltip';
import {
  tableCellContentBoxSx,
  tableCellPrimaryTextSx,
  tableCellSecondaryTextSx,
  tableCellTagChipSx,
  tableCellTagRowSx,
  tableCellTextBlockSx,
} from './sharedCellContent';
import type { TableTextLine } from '../tableTypes';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type TableTextCellProps = { primary: TableTextLine; secondary?: TableTextLine[] };
type TableNameCellProps = {
  primary: TableTextLine;
  secondary?: TableTextLine | null;
  tags?: Array<{ label: string; tone: React.ComponentProps<typeof Chip>['tone'] }>;
};
type RunLinkCellProps = {
  label: string;
  href?: string | null;
  latest?: boolean;
  onClick?: () => void;
  align?: 'left' | 'right';
};
type RelativeTimeTooltipCellProps = {
  value: Date | string | null | undefined;
  emptyLabel?: string;
};

const numericTextSx = {
  fontSize: '13px',
  lineHeight: 1.2,
};

const toSxArray = (value: SxProps<Theme> | undefined): Array<SxProps<Theme>> => {
  if (value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return [...value] as Array<SxProps<Theme>>;
  }

  return [value];
};

const buildLiteralSx = (
  prefersMonospaceLiterals: boolean,
  literal: boolean | undefined,
): Array<SxProps<Theme>> =>
  prefersMonospaceLiterals && literal ? [{ fontFamily: fontFamilies.mono }] : [];

export const TableTextCell = ({ primary, secondary }: TableTextCellProps): ReactElement => {
  const { prefersMonospaceLiterals } = useDisplayPreferences();
  const secondaryLines = secondary ?? [];
  const primarySx = toSxArray(primary.sx);
  const primaryRole = primary.role ?? 'body';
  const primaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, primary.literal);
  const primaryHref = primary.href;
  return (
    <Box sx={[tableCellTextBlockSx, { rowGap: '8px' }] as SxProps<Theme>}>
      {primaryHref ? (
        <Text role={primaryRole} sx={{ width: '100%', minWidth: 0 }}>
          <OverflowTooltip title={primary.label}>
            <MuiLink
              component={RouterLink}
              to={primaryHref}
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                primary.onClick?.();
              }}
              sx={[linkTruncateSx, ...primaryLiteralSx, ...primarySx] as SxProps<Theme>}
            >
              {primary.label}
            </MuiLink>
          </OverflowTooltip>
        </Text>
      ) : (
        <OverflowTooltip title={primary.label}>
          <Text
            role={primaryRole}
            truncate
            sx={
              [
                { display: 'block', width: '100%', minWidth: 0, maxWidth: '100%' },
                ...primaryLiteralSx,
                ...primarySx,
              ] as SxProps<Theme>
            }
          >
            {primary.label}
          </Text>
        </OverflowTooltip>
      )}
      {secondaryLines.map((line, index) => {
        const lineRole = line.role ?? 'body';
        const lineLiteralSx = buildLiteralSx(prefersMonospaceLiterals, line.literal);
        const lineSx = toSxArray(line.sx);
        return (
          <Text
            key={`${line.href ?? 'static'}-${line.label}-${String(index)}`}
            role={lineRole}
            color="textSecondary"
            truncate={!line.href}
            sx={[{ width: '100%', minWidth: 0 }, ...lineLiteralSx, ...lineSx] as SxProps<Theme>}
          >
            {line.href ? (
              <OverflowTooltip title={line.label}>
                <MuiLink
                  component={RouterLink}
                  to={line.href}
                  underline="hover"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    line.onClick?.();
                  }}
                  sx={[linkTruncateSx, ...lineLiteralSx, ...lineSx] as SxProps<Theme>}
                >
                  {line.label}
                </MuiLink>
              </OverflowTooltip>
            ) : (
              <OverflowTooltip title={line.label}>
                <span>{line.label}</span>
              </OverflowTooltip>
            )}
          </Text>
        );
      })}
    </Box>
  );
};

export const TableNameCell = ({
  primary,
  secondary = null,
  tags = [],
}: TableNameCellProps): ReactElement => {
  const { prefersMonospaceLiterals } = useDisplayPreferences();
  const primaryRole = primary.role ?? 'body';
  const primarySx = toSxArray(primary.sx);
  const primaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, primary.literal);
  const secondaryRole = secondary?.role ?? 'body';
  const secondarySx = toSxArray(secondary?.sx);
  const secondaryLiteralSx = buildLiteralSx(prefersMonospaceLiterals, secondary?.literal);

  return (
    <Box sx={tableCellTextBlockSx}>
      {primary.href ? (
        <Text role={primaryRole} dataGridTypography="custom" sx={tableCellPrimaryTextSx}>
          <OverflowTooltip title={primary.label}>
            <MuiLink
              component={RouterLink}
              to={primary.href}
              data-grid-typography="custom"
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                primary.onClick?.();
              }}
              sx={[linkTruncateSx, ...primaryLiteralSx, ...primarySx] as SxProps<Theme>}
            >
              {primary.label}
            </MuiLink>
          </OverflowTooltip>
        </Text>
      ) : (
        <OverflowTooltip title={primary.label}>
          <Text
            role={primaryRole}
            dataGridTypography="custom"
            truncate
            sx={
              [
                tableCellPrimaryTextSx,
                { display: 'block' },
                ...primaryLiteralSx,
                ...primarySx,
              ] as SxProps<Theme>
            }
          >
            {primary.label}
          </Text>
        </OverflowTooltip>
      )}
      {secondary ? (
        secondary.href ? (
          <Text
            role={secondaryRole}
            dataGridTypography="custom"
            color="textSecondary"
            sx={tableCellSecondaryTextSx}
          >
            <OverflowTooltip title={secondary.label}>
              <MuiLink
                component={RouterLink}
                to={secondary.href}
                data-grid-typography="custom"
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                  secondary.onClick?.();
                }}
                sx={[linkTruncateSx, ...secondaryLiteralSx, ...secondarySx] as SxProps<Theme>}
              >
                {secondary.label}
              </MuiLink>
            </OverflowTooltip>
          </Text>
        ) : (
          <OverflowTooltip title={secondary.label}>
            <Text
              role={secondaryRole}
              dataGridTypography="custom"
              color="textSecondary"
              truncate
              sx={
                [
                  tableCellSecondaryTextSx,
                  { display: 'block' },
                  ...secondaryLiteralSx,
                  ...secondarySx,
                ] as SxProps<Theme>
              }
            >
              {secondary.label}
            </Text>
          </OverflowTooltip>
        )
      ) : null}
      {tags.length > 0 ? (
        <Box sx={[tableCellTagRowSx, { mt: s('sm') }]}>
          <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
            {tags.map((tag, index) => (
              <Chip
                key={`${tag.label}-${String(index)}`}
                size="xs"
                tone={tag.tone}
                label={tag.label}
                sx={tableCellTagChipSx}
              />
            ))}
          </Inline>
        </Box>
      ) : null}
    </Box>
  );
};

export const RunLinkCell = ({
  label,
  href,
  latest = false,
  onClick,
  align = 'left',
}: RunLinkCellProps): ReactElement => {
  const runNode = href ? (
    <OverflowTooltip title={label} inline>
      <MuiLink
        component={RouterLink}
        to={href}
        underline="hover"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        sx={[linkTruncateSx, numericTextSx] as SxProps<Theme>}
      >
        {label}
      </MuiLink>
    </OverflowTooltip>
  ) : (
    <OverflowTooltip title={label} inline>
      <Text role="body" component="span" sx={[linkTruncateSx, numericTextSx] as SxProps<Theme>}>
        {label}
      </Text>
    </OverflowTooltip>
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Inline
        gap="md"
        align="center"
        sx={{
          width: '100%',
          minWidth: 0,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
      >
        <Text role="body" component="span" sx={{ minWidth: 0, ...numericTextSx }}>
          {runNode}
        </Text>
        {latest ? (
          <Chip
            label="Latest"
            tone="primary"
            size="xs"
            sx={{
              borderRadius: r('pill'),
              '& .MuiChip-label .MuiTypography-root': {
                fontSize: '13px',
                lineHeight: 1.2,
              },
            }}
          />
        ) : null}
      </Inline>
    </Box>
  );
};

export const RelativeTimeTooltipCell = ({
  value,
  emptyLabel = '-',
}: RelativeTimeTooltipCellProps): ReactElement => {
  const timestamp = value ?? null;
  const label = timestamp ? formatRelativeTime(timestamp) : emptyLabel;
  const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
  return (
    <Tooltip
      title={fullTimestamp}
      disableHoverListener={!fullTimestamp}
      enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      arrow
      slotProps={{
        tooltip: { sx: { fontSize: '15px', lineHeight: 1.4 } },
      }}
    >
      <Box component="span" sx={tableCellContentBoxSx}>
        <Text role="body" component="span" sx={numericTextSx}>
          {label.length > 0 ? label : emptyLabel}
        </Text>
      </Box>
    </Tooltip>
  );
};
